import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ConnectionStatus } from '../../types/connections';
import { Pipeline } from '../../types/pipelines';
import { apiFetch } from '../../services/api';
import { PipelineFormLayout } from '../PipelineFormLayout';

const GA4SessionizationForm: React.FC = () => {
    const navigate = useNavigate();
    const { id } = useParams<{ id: string }>();
    const isEditing = !!id;

    const [pipelineName, setPipelineName] = useState('');
    const [selectedGBQConnection, setSelectedGBQConnection] = useState<string>('');
    const [sessionTimeout, setSessionTimeout] = useState<number>(30);
    const [connectionStatus, setConnectionStatus] = useState<ConnectionStatus | null>(null);
    const [bqProjects, setBqProjects] = useState<{ id: string; name: string }[]>([]);
    const [selectedProject, setSelectedProject] = useState<string>('');
    const [bqDatasets, setBqDatasets] = useState<string[]>([]);
    const [selectedDataset, setSelectedDataset] = useState<string>('');
    const [loadingBQ, setLoadingBQ] = useState(false);
    const [bqError, setBqError] = useState<string | null>(null);

    useEffect(() => {
        loadConnectionStatus();
        if (isEditing) {
            loadPipeline(id!);
        }
    }, [id, isEditing]);

    const loadConnectionStatus = async () => {
        try {
            const res = await apiFetch('/api/connections/status');
            if (res.ok) {
                setConnectionStatus(await res.json());
            }
        } catch (error) {
            console.error('Failed to load connection status:', error);
        }
    };

    const loadPipeline = async (pipelineId: string) => {
        try {
            const res = await apiFetch(`/api/pipelines/${pipelineId}`);
            if (res.ok) {
                const pipeline: Pipeline = await res.json();
                setPipelineName(pipeline.name);
                if (pipeline.connections?.gbqConnectionId) setSelectedGBQConnection(pipeline.connections.gbqConnectionId);
                if (pipeline.parameters?.sessionTimeout) setSessionTimeout(pipeline.parameters.sessionTimeout);
                if (pipeline.dataSources?.ga4_export?.project) setSelectedProject(pipeline.dataSources.ga4_export.project);
                else if (pipeline.parameters?.ga4Project) setSelectedProject(pipeline.parameters.ga4Project);
                if (pipeline.dataSources?.ga4_export?.dataset) setSelectedDataset(pipeline.dataSources.ga4_export.dataset);
                else if (pipeline.parameters?.ga4Dataset) setSelectedDataset(pipeline.parameters.ga4Dataset);
            }
        } catch (error) {
            console.error('Failed to load pipeline:', error);
        }
    };

    const fetchBQProjects = useCallback(async () => {
        if (!selectedGBQConnection) return;
        setLoadingBQ(true);
        setBqError(null);
        try {
            const response = await apiFetch(`/api/connections/bigquery/projects?gbqConnectionId=${selectedGBQConnection}`);
            if (!response.ok) throw new Error('Failed to fetch projects');
            const data = await response.json();
            const projects = Array.isArray(data) ? data.map((p: any) =>
                typeof p === 'string' ? { id: p, name: p } : p
            ) : [];
            setBqProjects(projects);
        } catch (error: any) {
            setBqError(error.message);
        } finally {
            setLoadingBQ(false);
        }
    }, [selectedGBQConnection]);

    const fetchBQDatasets = useCallback(async (projectId: string) => {
        if (!selectedGBQConnection) return;
        setLoadingBQ(true);
        setBqError(null);
        try {
            const response = await apiFetch(`/api/connections/bigquery/datasets?projectId=${projectId}&gbqConnectionId=${selectedGBQConnection}`);
            if (!response.ok) throw new Error('Failed to fetch datasets');
            const data = await response.json();
            const datasets = Array.isArray(data) ? data : [];
            setBqDatasets(datasets);
        } catch (error: any) {
            setBqError(error.message);
        } finally {
            setLoadingBQ(false);
        }
    }, [selectedGBQConnection]);

    useEffect(() => {
        if (selectedGBQConnection) {
            fetchBQProjects();
        }
    }, [selectedGBQConnection, fetchBQProjects]);

    useEffect(() => {
        if (selectedProject && selectedGBQConnection) {
            setSelectedDataset('');
            fetchBQDatasets(selectedProject);
        }
    }, [selectedProject, selectedGBQConnection, fetchBQDatasets]);

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();

        const pipelineData = {
            type: 'ga4-sessionization' as const,
            name: pipelineName || 'GA4 Sessionization Pipeline',
            connections: {
                gbqConnectionId: selectedGBQConnection
            },
            dataSources: {
                ga4_export: {
                    project: selectedProject,
                    dataset: selectedDataset
                }
            },
            parameters: {
                sessionTimeout
            }
        };

        try {
            const method = isEditing ? 'PUT' : 'POST';
            const url = isEditing ? `/api/pipelines/${id}` : '/api/pipelines';

            const res = await apiFetch(url, {
                method,
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(pipelineData)
            });

            if (res.ok) {
                navigate('/pipelines');
            } else {
                throw new Error('Failed to save pipeline');
            }
        } catch (error) {
            console.error('Error saving pipeline:', error);
            alert('Failed to save pipeline');
        }
    };

    return (
        <PipelineFormLayout isEditing={isEditing} pipelineType="ga4-sessionization">
            <form onSubmit={handleSubmit}>
                <div className="form-section">
                    <h3>BigQuery Connector</h3>
                    <div className="form-group">
                        <label>BigQuery Connection</label>
                        <select
                            value={selectedGBQConnection}
                            onChange={e => setSelectedGBQConnection(e.target.value)}
                            required
                        >
                            <option value="">Select Connection</option>
                            {connectionStatus?.gbq?.map(c => (
                                <option key={c.id} value={c.id}>{c.email}</option>
                            ))}
                        </select>
                    </div>
                    {selectedGBQConnection && (
                        <>
                            {bqError && <div className="error-message">{bqError}</div>}
                            <div className="form-group">
                                <label className="required">Project</label>
                                <select
                                    value={selectedProject}
                                    onChange={e => {
                                        setSelectedProject(e.target.value);
                                        setSelectedDataset('');
                                        if (!pipelineName && e.target.value && bqProjects.length > 0) {
                                            const project = bqProjects.find(p => p.id === e.target.value);
                                            if (project) {
                                                setPipelineName(project.name);
                                            }
                                        }
                                    }}
                                    required
                                    disabled={loadingBQ}
                                >
                                    <option value="">{loadingBQ ? 'Loading...' : 'Select Project'}</option>
                                    {bqProjects.map(p => (
                                        <option key={p.id} value={p.id}>{p.name} ({p.id})</option>
                                    ))}
                                </select>
                            </div>
                            <div className="form-group">
                                <label className="required">Dataset</label>
                                <select
                                    value={selectedDataset}
                                    onChange={e => setSelectedDataset(e.target.value)}
                                    required
                                    disabled={loadingBQ || !selectedProject}
                                >
                                    <option value="">{loadingBQ ? 'Loading...' : 'Select Dataset'}</option>
                                    {bqDatasets.map(d => (
                                        <option key={d} value={d}>{d}</option>
                                    ))}
                                </select>
                            </div>
                        </>
                    )}
                </div>

                <div className="form-section">
                    <div className="form-group">
                        <label>Session timeout, minutes</label>
                        <input
                            type="number"
                            value={sessionTimeout}
                            onChange={e => setSessionTimeout(parseInt(e.target.value) || 30)}
                            min="1"
                            placeholder="30"
                        />
                    </div>
                    <div className="form-group">
                        <label>Name</label>
                        <input
                            type="text"
                            value={pipelineName}
                            onChange={e => setPipelineName(e.target.value)}
                            placeholder="Pipeline Name"
                        />
                    </div>
                </div>

                <div className="form-actions">
                    <button type="submit" className="primary-button">Save Pipeline</button>
                </div>
            </form>
        </PipelineFormLayout>
    );
};

export default GA4SessionizationForm;
